import { useEffect } from 'react';

export const SITE_URL = 'https://aplusautoglass.ca';
export const SITE_NAME = 'A+ Auto Glass';
// Social share preview image (van in front of Parliament Hill, Ottawa)
export const OG_IMAGE =
  'https://customer-assets.emergentagent.com/job_glass-quote-hub-1/artifacts/1cc3qzta_Van%20with%20Parliment.png';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Sets title, description, canonical, Open Graph and Twitter tags for a page.
export function useSeo({ title, description, path = '/', image = OG_IMAGE, type = 'website' }) {
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
  }, [title, description, path, image, type]);
}

// Injects a JSON-LD structured-data script, removed on unmount.
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [id, data]);
}

// LocalBusiness / AutoRepair schema for the homepage.
export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'AutoRepair',
  '@id': `${SITE_URL}/#business`,
  name: SITE_NAME,
  image: OG_IMAGE,
  url: SITE_URL,
  telephone: '+1-343-558-8820',
  email: 'info@aplusautoglass.ca',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '8-1439 Youville Dr',
    addressLocality: 'Orleans',
    addressRegion: 'ON',
    postalCode: 'K1C 4M8',
    addressCountry: 'CA',
  },
  areaServed: ['Ottawa', 'Orléans', 'Kanata', 'Gatineau', 'Nepean', 'Barrhaven'],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '45',
  },
  makesOffer: [
    'Windshield Replacement',
    'Rock Chip Repair',
    'ADAS Calibration',
    'Mobile Auto Glass Service',
    'Door & Back Glass Replacement',
  ],
};

export const FAQ_ITEMS = [
  { q: 'How long does windshield replacement take?', a: 'Most replacements take 60–90 minutes, plus about an hour of safe drive-away time for the adhesive to cure.' },
  { q: 'Do you offer mobile service?', a: 'Yes. We come to your home or workplace across Ottawa, Orléans, Gatineau and surrounding areas at no extra charge.' },
  { q: 'Do you calibrate ADAS cameras?', a: 'Yes. We perform static and dynamic ADAS calibration in-house so your safety systems work exactly as the manufacturer intended.' },
  { q: 'Can you repair stone chips?', a: 'Absolutely. Chips smaller than a loonie can usually be repaired in under 30 minutes, often fully covered by insurance.' },
  { q: 'Do you provide a warranty?', a: 'Every installation is backed by our lifetime workmanship warranty against leaks and defects for as long as you own the vehicle.' },
];

export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
};
